import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react'
import clsx from 'clsx'
import { registerToast, unregisterToast } from '../../lib/globalToast'

type ToastType = 'success' | 'error' | 'info'

interface Toast {
  id: string
  type: ToastType
  message: string
  duration?: number
}

interface ToastContextType {
  showToast: (type: ToastType, message: string, duration?: number) => void
}

const ToastContext = createContext<ToastContextType | null>(null)

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

interface ToastProviderProps {
  children: ReactNode
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const showToast = useCallback((type: ToastType, message: string, duration = 4000) => {
    const id = Math.random().toString(36).substr(2, 9)
    const toast: Toast = { id, type, message, duration }

    setToasts(prev => [...prev, toast])

    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id))
      }, duration)
    }
  }, [])

  // Register showToast globally so non-React code (MutationCache) can trigger toasts
  useEffect(() => {
    registerToast(showToast)
    return () => unregisterToast()
  }, [showToast])

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-2 pointer-events-none">
        {toasts.map(toast => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onRemove={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

interface ToastItemProps {
  toast: Toast
  onRemove: () => void
}

function ToastItem({ toast, onRemove }: ToastItemProps) {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    setIsVisible(true)
    const timer = setTimeout(() => setIsVisible(false), (toast.duration || 4000) - 300)
    return () => clearTimeout(timer)
  }, [toast.duration])

  const typeStyles = {
    success: 'bg-praxi-green-bg border-praxi-green/20 text-praxi-green',
    error: 'bg-coral-bg border-coral/20 text-coral',
    info: 'bg-azure-bg border-azure/20 text-azure',
  }

  return (
    <div
      role="alert"
      data-testid={`toast-${toast.type}`}
      className={clsx(
        'pointer-events-auto max-w-sm p-4 border rounded-lg shadow-lg transition-all duration-300 transform',
        typeStyles[toast.type],
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
      )}
    >
      <div className="flex items-start justify-between">
        <p className="text-sm font-medium">{toast.message}</p>
        <button
          onClick={onRemove}
          className="ms-4 text-current opacity-70 hover:opacity-100 transition-opacity"
        >
          ×
        </button>
      </div>
    </div>
  )
}